import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Field, SelectField } from "@/components/common";
import { EntityDialog } from "@/components/EntityDialog";
import { api } from "@/lib/api";
import { useList } from "@/lib/hooks";
import { EQUIPMENT_TYPES, SPEC_FIELDS, CONTRACT_TYPES, CONTRACT_STATUS, PERIODS, todayStr } from "@/lib/constants";

const T = ({ f, set, k, testid, ...rest }) => (
  <Input value={f[k] ?? ""} onChange={(e) => set(k)(e.target.value)} data-testid={testid} {...rest} />
);
const D = ({ f, set, k, testid }) => (
  <Input type="date" value={f[k] || ""} onChange={(e) => set(k)(e.target.value || null)} data-testid={testid} />
);

const ClientPick = ({ f, set, open, testid }) => {
  const { items } = useList(open ? "/clients" : "/", undefined);
  const clients = Array.isArray(items) ? items.map((c) => [c.id, c.name]) : [];
  return <SelectField value={f.client_id} onChange={set("client_id")} options={clients} testid={testid} placeholder="Choisir un client" />;
};

const Duplicates = ({ f }) => {
  const [dups, setDups] = useState([]);
  useEffect(() => {
    if (!f.client_id || (!f.serial && !(f.brand && f.model))) { setDups([]); return undefined; }
    const t = setTimeout(() => api.get("/equipment/duplicates", { params: { client_id: f.client_id, serial: f.serial || "", brand: f.brand || "", model: f.model || "", exclude: f.id || "" } })
      .then((r) => setDups(r.data)).catch(() => setDups([])), 500);
    return () => clearTimeout(t);
  }, [f.client_id, f.serial, f.brand, f.model, f.id]);
  if (!dups.length) return null;
  return (
    <div className="flex gap-2 rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 text-xs text-amber-100 sm:col-span-2" data-testid="equipment-duplicate-warning">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>Équipement peut-être déjà enregistré : {dups.map((d) => `${d.name || d.brand} ${d.model} ${d.serial ? `(n° ${d.serial})` : ""} – ${d.client_name}`).join(" · ")}</span>
    </div>
  );
};

const EquipmentFields = ({ f, set, open, fixedClient }) => {
  const [more, setMore] = useState(false);
  const setSpec = (k) => (v) => set("specs")({ ...(f.specs || {}), [k]: v });
  return (
    <>
      {!fixedClient && <Field label="Client *" className="sm:col-span-2"><ClientPick f={f} set={set} open={open} testid="equipment-client-select" /></Field>}
      <Field label="Type *"><SelectField value={f.type} onChange={set("type")} options={EQUIPMENT_TYPES} testid="equipment-type-select" /></Field>
      <Field label="Nom personnalisé"><T f={f} set={set} k="name" testid="equipment-name-input" placeholder="Ex. PC accueil" /></Field>
      <Field label="Marque"><T f={f} set={set} k="brand" testid="equipment-brand-input" /></Field>
      <Field label="Modèle"><T f={f} set={set} k="model" testid="equipment-model-input" /></Field>
      <Field label="N° de série"><T f={f} set={set} k="serial" testid="equipment-serial-input" /></Field>
      <Field label="Emplacement"><T f={f} set={set} k="location" testid="equipment-location-input" placeholder="Ex. bureau 2" /></Field>
      <Duplicates f={f} />
      <button type="button" onClick={() => setMore(!more)} className="text-left text-sm text-cyan-300 sm:col-span-2" data-testid="equipment-more-toggle">
        {more ? "Masquer" : "Afficher"} les informations techniques et garantie
      </button>
      {more && (
        <>
          {Object.entries(SPEC_FIELDS).map(([k, label]) => (
            <Field key={k} label={label}><Input value={(f.specs || {})[k] || ""} onChange={(e) => setSpec(k)(e.target.value)} data-testid={`equipment-spec-${k}`} /></Field>
          ))}
          <Field label="Date d'achat"><D f={f} set={set} k="purchase_date" testid="equipment-purchase-input" /></Field>
          <Field label="Fin de garantie"><D f={f} set={set} k="warranty_end" testid="equipment-warranty-input" /></Field>
          <Field label="Observations / notes internes" className="sm:col-span-2">
            <Textarea value={f.notes || ""} onChange={set("notes")} data-testid="equipment-notes-input" />
          </Field>
        </>
      )}
    </>
  );
};

export const EquipmentDialog = (p) => (
  <EntityDialog {...p} resource="equipment" title="équipement" testid="equipment-dialog"
    defaults={{ client_id: "", type: "pc_fixe", name: "", brand: "", model: "", serial: "", location: "", specs: {}, notes: "", purchase_date: null, warranty_end: null, archived: false }}
    render={(f, set) => <EquipmentFields f={f} set={set} open={p.open} fixedClient={p.fixedClient} />} />
);

const EquipmentPicker = ({ clientId, value, onChange }) => {
  const { items } = useList(clientId ? "/equipment" : "/", clientId ? { client_id: clientId } : undefined);
  const list = Array.isArray(items) ? items : [];
  if (!clientId) return <p className="text-xs text-slate-400">Choisissez d'abord le client.</p>;
  if (!list.length) return <p className="text-xs text-slate-400">Aucun équipement enregistré pour ce client.</p>;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="grid gap-1 sm:grid-cols-2" data-testid="contract-equipment-picker">
      {list.map((e) => (
        <label key={e.id} className="flex items-center gap-2 rounded border border-white/10 px-2 py-1.5 text-sm text-slate-200">
          <input type="checkbox" checked={value.includes(e.id)} onChange={() => toggle(e.id)} data-testid={`contract-equipment-${e.id}`} />
          {e.name || `${e.brand} ${e.model}`}
        </label>
      ))}
    </div>
  );
};

const ContractFields = ({ f, set, open }) => {
  const [tasks, setTasks] = useState(null);
  useEffect(() => {
    if (open && f.preventive_enabled && !(f.preventive_tasks || []).length) {
      api.get("/settings/v3").then((r) => { setTasks(r.data.maintenance_tasks); set("preventive_tasks")(r.data.maintenance_tasks); });
    }
  }, [open, f.preventive_enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  const hours = f.type === "credit_heures" || f.type === "mixte";
  return (
    <>
      <Field label="Nom du contrat *" className="sm:col-span-2"><T f={f} set={set} k="name" testid="contract-name-input" required placeholder="Ex. Maintenance annuelle cabinet" /></Field>
      <Field label="Client *" className="sm:col-span-2"><ClientPick f={f} set={set} open={open} testid="contract-client-select" /></Field>
      <Field label="Type *"><SelectField value={f.type} onChange={set("type")} options={CONTRACT_TYPES} testid="contract-type-select" /></Field>
      <Field label="Statut"><SelectField value={f.status} onChange={set("status")} options={CONTRACT_STATUS} testid="contract-status-select" /></Field>
      <Field label="Début *"><D f={f} set={set} k="start_date" testid="contract-start-input" /></Field>
      <Field label="Fin"><D f={f} set={set} k="end_date" testid="contract-end-input" /></Field>
      <Field label="Montant (€)">
        <Input type="number" step="0.01" min={0} value={f.amount ?? ""} onChange={(e) => set("amount")(e.target.value === "" ? null : Number(e.target.value))} data-testid="contract-amount-input" />
      </Field>
      <Field label="Facturation"><SelectField value={f.billing_frequency} onChange={set("billing_frequency")} options={{ ...PERIODS, unique: "Unique" }} testid="contract-billing-frequency-select" /></Field>
      {hours && (
        <>
          <Field label="Heures incluses par période">
            <Input type="number" step="0.25" min={0} value={f.hours_included ?? ""} onChange={(e) => set("hours_included")(e.target.value === "" ? null : Number(e.target.value))} data-testid="contract-hours-input" />
          </Field>
          <Field label="Période du crédit"><SelectField value={f.period} onChange={set("period")} options={PERIODS} testid="contract-period-select" /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-200 sm:col-span-2">
            <Switch checked={!!f.carry_over} onCheckedChange={set("carry_over")} data-testid="contract-carry-switch" />Reporter les heures non utilisées sur la période suivante
          </label>
        </>
      )}
      <Field label="Site / établissement" className="sm:col-span-2"><T f={f} set={set} k="site" testid="contract-site-input" /></Field>
      <Field label="Équipements couverts" className="sm:col-span-2">
        <EquipmentPicker clientId={f.client_id} value={f.equipment_ids || []} onChange={set("equipment_ids")} />
      </Field>
      <Field label="Prestations incluses" className="sm:col-span-2"><Textarea value={f.included || ""} onChange={set("included")} data-testid="contract-included-input" /></Field>
      <Field label="Exclusions" className="sm:col-span-2"><Textarea value={f.exclusions || ""} onChange={set("exclusions")} data-testid="contract-exclusions-input" /></Field>
      <Field label="Déplacements"><T f={f} set={set} k="travel_terms" testid="contract-travel-input" placeholder="Ex. inclus 0–15 km" /></Field>
      <Field label="Matériel"><T f={f} set={set} k="material_terms" testid="contract-material-input" placeholder="Ex. facturé en sus" /></Field>
      <label className="flex items-center gap-2 text-sm text-slate-200 sm:col-span-2">
        <Switch checked={!!f.preventive_enabled} onCheckedChange={set("preventive_enabled")} data-testid="contract-preventive-switch" />Maintenance préventive programmée
      </label>
      {f.preventive_enabled && (
        <>
          <Field label="Fréquence"><SelectField value={f.preventive_frequency} onChange={set("preventive_frequency")} options={{ ...PERIODS, personnalise: "Personnalisée" }} testid="contract-preventive-frequency-select" /></Field>
          {f.preventive_frequency === "personnalise" ? (
            <Field label="Tous les (jours)"><Input type="number" min={1} value={f.preventive_every_days} onChange={(e) => set("preventive_every_days")(Number(e.target.value))} data-testid="contract-preventive-days-input" /></Field>
          ) : <span />}
          <Field label="Prochaine maintenance"><D f={f} set={set} k="preventive_next_date" testid="contract-preventive-next-input" /></Field>
          <Field label="Tâches (une par ligne)" className="sm:col-span-2">
            <Textarea rows={5} value={(f.preventive_tasks || tasks || []).join("\n")} onChange={(e) => set("preventive_tasks")(e.target.value.split("\n"))} data-testid="contract-preventive-tasks-input" />
          </Field>
        </>
      )}
      <Field label="Observations et clauses particulières" className="sm:col-span-2"><Textarea value={f.notes || ""} onChange={set("notes")} data-testid="contract-notes-input" /></Field>
    </>
  );
};

const cleanTasks = (f) => ({ ...f, preventive_tasks: (f.preventive_tasks || []).map((t) => t.trim()).filter(Boolean) });

export const ContractDialog = (p) => (
  <EntityDialog {...p} initial={p.initial} resource="contracts" title="contrat" testid="contract-dialog"
    confirmBeforeSave={(f) => { Object.assign(f, cleanTasks(f)); return null; }}
    defaults={{ name: "", client_id: "", type: "forfait", status: "brouillon", start_date: todayStr(), end_date: null, period: "annuel", amount: null,
      billing_frequency: "annuel", included: "", exclusions: "", equipment_ids: [], hours_included: null, carry_over: false, travel_terms: "",
      material_terms: "", notes: "", preventive_enabled: false, preventive_frequency: "trimestriel", preventive_every_days: 90, preventive_next_date: null,
      preventive_tasks: [], site: "", archived: false }}
    render={(f, set) => <ContractFields f={f} set={set} open={p.open} />} />
);
